/* © NXAPP Limited */
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { authedFetch } from '@/lib/authedFetch'
import { describeError } from '@/lib/describeError'
import { Radar, Plus, Play, Trash2, Check, X, Search } from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import TrendStrip from '@/components/dashboard/TrendStrip'
import RowSplit from '@/components/dashboard/RowSplit'
import { dayKeys, bucketCount, emptySplit, addToSplit, londonDayKey, type Split } from '@/lib/trend'

interface Step { campaign_id: string; delay_days: number }

interface SearchRow {
  id: string; name: string; query: string; campaign_id: string | null
  location: string | null; company_size: string | null; target_roles: string[]
  promote_hour: number; promote_minute: number; promote_end_hour: number; promote_end_minute: number; promote_weekdays_only: boolean
  daily_target: number; hourly_target: number; max_pool: number; per_company_daily: number
  include_generic: boolean; include_personal: boolean; min_confidence: string; status: string; last_run_at: string | null; last_run_note: string | null
}

const ROLE_PRESETS = ['Founder', 'CEO', 'Managing Director', 'Director', 'Partner', 'Owner', 'Head of Dept']
interface Lead {
  id: string; company_name: string | null; name: string | null; role: string | null
  email: string; email_type: string; verified: boolean; confidence: string | null; comment: string | null; status: string; added_at: string | null
}

export default function ProspectPage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [error, setError] = useState('')
  const [selId, setSelId] = useState<string | null>(null)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<'pending' | 'added' | 'all'>('pending')
  const [showNew, setShowNew] = useState(false)
  const [form, setForm] = useState({ name: '', query: '' })

  const { data: campaigns } = useQuery({
    queryKey: ['prospect-campaigns', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('campaigns').select('id, name, kind').eq('user_id', user!.id).order('name')
      return (data ?? []) as { id: string; name: string; kind: string }[]
    },
  })

  const { data: searches } = useQuery({
    queryKey: ['prospect-searches', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('prospect_searches').select('*').eq('user_id', user!.id).order('created_at', { ascending: false })
      const rows = (data ?? []) as SearchRow[]
      if (!selId && rows.length) setSelId(rows[0].id)
      return rows
    },
  })
  const sel = (searches ?? []).find(s => s.id === selId) ?? null

  const { data: leads } = useQuery({
    queryKey: ['prospect-leads', selId],
    enabled: !!selId,
    queryFn: async () => {
      const { data } = await supabase.from('prospect_leads')
        .select('id, company_name, name, role, email, email_type, verified, confidence, comment:ai_verdict, status, added_at')
        .eq('search_id', selId!).order('created_at', { ascending: false }).limit(1000)
      return (data ?? []) as Lead[]
    },
  })

  // Stats + last-10 activity for the selected search.
  const { data: stats } = useQuery({
    queryKey: ['prospect-stats', selId, leads?.length],
    enabled: !!selId,
    queryFn: async () => {
      const head = { count: 'exact' as const, head: true }
      const [companies, enriched, leadsC, added, pending] = await Promise.all([
        supabase.from('prospect_companies').select('id', head).eq('search_id', selId!),
        supabase.from('prospect_companies').select('id', head).eq('search_id', selId!).eq('status', 'enriched'),
        supabase.from('prospect_leads').select('id', head).eq('search_id', selId!),
        supabase.from('prospect_leads').select('id', head).eq('search_id', selId!).eq('status', 'added'),
        supabase.from('prospect_leads').select('id', head).eq('search_id', selId!).eq('status', 'pending'),
      ])
      return { companies: companies.count ?? 0, enriched: enriched.count ?? 0, leads: leadsC.count ?? 0, added: added.count ?? 0, pending: pending.count ?? 0 }
    },
  })
  const { data: activity } = useQuery({
    queryKey: ['prospect-activity', selId, leads?.length],
    enabled: !!selId,
    queryFn: async () => {
      const { data } = await supabase.from('prospect_activity').select('kind, note, discovered, leads, added, created_at').eq('search_id', selId!).order('created_at', { ascending: false }).limit(10)
      return (data ?? []) as { kind: string; note: string; discovered: number; leads: number; added: number; created_at: string }[]
    },
  })
  // Module dashboard — companies found / emails found / added-to-campaign over
  // the last 30 days, both aggregated (top strip) and split per search (list).
  const { data: trend, isLoading: trendLoading } = useQuery({
    queryKey: ['prospect-trend', user?.id],
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const since = new Date(Date.now() - 30 * 86_400_000).toISOString()
      // Source-of-truth tables (not the activity log) so every path counts and
      // history back-fills: companies by created_at, emails (leads) by created_at,
      // adds by added_at (set on every step-1 placement — cron OR manual).
      const [companiesR, leadsR, addedR] = await Promise.all([
        supabase.from('prospect_companies').select('search_id, created_at').eq('user_id', user!.id).gte('created_at', since).limit(10000),
        supabase.from('prospect_leads').select('search_id, created_at').eq('user_id', user!.id).gte('created_at', since).limit(10000),
        supabase.from('prospect_leads').select('search_id, added_at').eq('user_id', user!.id).eq('status', 'added').not('added_at', 'is', null).gte('added_at', since).limit(10000),
      ])
      const companies = (companiesR.data ?? []) as { search_id: string; created_at: string }[]
      const leads = (leadsR.data ?? []) as { search_id: string; created_at: string }[]
      const added = (addedR.data ?? []) as { search_id: string; added_at: string }[]
      const keys = dayKeys(30)
      const nowMs = Date.now(); const todayKey = londonDayKey(new Date())
      const bySearch: Record<string, { companies: Split; emails: Split; added: Split }> = {}
      const row = (id: string) => (bySearch[id] ??= { companies: emptySplit(), emails: emptySplit(), added: emptySplit() })
      for (const c of companies) addToSplit(row(c.search_id).companies, c.created_at, 1, nowMs, todayKey)
      for (const l of leads) addToSplit(row(l.search_id).emails, l.created_at, 1, nowMs, todayKey)
      for (const a of added) addToSplit(row(a.search_id).added, a.added_at, 1, nowMs, todayKey)
      return {
        metrics: [
          { label: 'Companies found', tone: 'indigo' as const, series: bucketCount(companies, keys, r => r.created_at) },
          { label: 'Emails found', tone: 'teal' as const, series: bucketCount(leads, keys, r => r.created_at) },
          { label: 'Added to campaign', tone: 'green' as const, series: bucketCount(added, keys, r => r.added_at) },
        ],
        bySearch,
      }
    },
  })

  // Why aren't pending leads auto-adding? Same gate the cron uses.
  const { data: pendingLeads } = useQuery({
    queryKey: ['prospect-pending', selId, leads?.length],
    enabled: !!selId,
    queryFn: async () => {
      const { data } = await supabase.from('prospect_leads').select('id, email_type, verified, confidence').eq('search_id', selId!).eq('status', 'pending').limit(1000)
      return (data ?? []) as { id: string; email_type: string; verified: boolean; confidence: string | null }[]
    },
  })
  const readiness = (() => {
    if (!sel || !pendingLeads) return null
    const levels = sel.min_confidence === 'verified' ? ['verified'] : ['verified', 'pattern']
    const toggleOk = (t: string) => (t === 'generic' ? sel.include_generic : sel.include_personal)
    const notVerified = pendingLeads.filter(l => !l.verified).length
    const verifiedRows = pendingLeads.filter(l => l.verified)
    const belowConf = verifiedRows.filter(l => !levels.includes(l.confidence ?? '')).length
    const confOk = verifiedRows.filter(l => levels.includes(l.confidence ?? ''))
    const wrongToggle = confOk.filter(l => !toggleOk(l.email_type)).length
    const qualifiedRows = confOk.filter(l => toggleOk(l.email_type))
    return { total: pendingLeads.length, qualified: qualifiedRows.length, qualifiedIds: qualifiedRows.map(l => l.id), notVerified, belowConf, wrongToggle }
  })()

  // Campaign routing steps for the selected search.
  const { data: stepRows } = useQuery({
    queryKey: ['prospect-steps', selId],
    enabled: !!selId,
    queryFn: async () => {
      const { data } = await supabase.from('prospect_search_campaigns')
        .select('campaign_id, delay_days, step_no').eq('search_id', selId!).order('step_no')
      return (data ?? []) as { campaign_id: string; delay_days: number; step_no: number }[]
    },
  })

  // Which campaign(s) each added lead has been placed into.
  const { data: placements } = useQuery({
    queryKey: ['prospect-placements', selId],
    enabled: !!selId,
    queryFn: async () => {
      const { data } = await supabase.from('prospect_lead_placements')
        .select('lead_id, campaign_id, step_no').eq('search_id', selId!)
      const map = new Map<string, { campaign_id: string; step_no: number }[]>()
      for (const r of (data ?? []) as { lead_id: string; campaign_id: string; step_no: number }[]) {
        const arr = map.get(r.lead_id) ?? []; arr.push(r); map.set(r.lead_id, arr)
      }
      return map
    },
  })

  // Editable local copy of the routing steps (seeded from the query / fallback).
  const [steps, setSteps] = useState<Step[]>([])
  useEffect(() => {
    if (!selId) return
    if (stepRows && stepRows.length) setSteps(stepRows.map(r => ({ campaign_id: r.campaign_id, delay_days: r.delay_days })))
    else setSteps([{ campaign_id: sel?.campaign_id ?? '', delay_days: 0 }])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, stepRows])

  const campName = (id: string) => campaigns?.find(c => c.id === id)?.name ?? 'campaign'

  const saveSteps = useMutation({
    mutationFn: async (next: Step[]) => {
      const clean = next.filter(s => s.campaign_id)
      // Rewrite the whole ordered list, then keep campaign_id in sync with step 1.
      await supabase.from('prospect_search_campaigns').delete().eq('search_id', selId!)
      if (clean.length) {
        await supabase.from('prospect_search_campaigns').insert(clean.map((s, i) => ({
          user_id: user!.id, search_id: selId!, campaign_id: s.campaign_id, step_no: i + 1, delay_days: i === 0 ? 0 : Math.max(0, s.delay_days || 0),
        })))
      }
      await supabase.from('prospect_searches').update({ campaign_id: clean[0]?.campaign_id ?? null }).eq('id', selId!)
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['prospect-steps', selId] }); qc.invalidateQueries({ queryKey: ['prospect-searches'] }) },
  })

  const createSearch = useMutation({
    mutationFn: async () => {
      if (!form.name.trim() || !form.query.trim()) throw new Error('Give it a name and a description of who to find.')
      const { data, error } = await supabase.from('prospect_searches')
        .insert({ user_id: user!.id, name: form.name.trim(), query: form.query.trim() }).select('id').single()
      if (error) throw error
      return data.id as string
    },
    onError: e => setError(describeError(e)),
    onSuccess: id => { setForm({ name: '', query: '' }); setShowNew(false); setSelId(id); qc.invalidateQueries({ queryKey: ['prospect-searches'] }) },
  })

  const updateSearch = useMutation({
    mutationFn: async (patch: Partial<SearchRow>) => {
      const { error } = await supabase.from('prospect_searches').update(patch).eq('id', selId!)
      if (error) throw error
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prospect-searches'] }),
  })

  const deleteSearch = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('prospect_searches').delete().eq('id', id); if (error) throw error },
    onSuccess: () => { setSelId(null); qc.invalidateQueries({ queryKey: ['prospect-searches'] }) },
  })

  const [note, setNote] = useState('')
  const runNow = useMutation({
    mutationFn: async () => {
      // Each call does ONE unit (one web-search); loop a few so one click yields
      // leads. Refresh the table between units so results stream in.
      const id = selId
      let last: { ok?: boolean; note?: string; done?: boolean; leads?: number } = {}
      for (let n = 0; n < 4; n++) {
        const res = await authedFetch('/api/prospect/run', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ searchId: id }) })
        last = await res.json()
        setNote(last.note ?? '')
        qc.invalidateQueries({ queryKey: ['prospect-leads', id] })
        if (!last.ok || last.done) break
      }
      return last
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['prospect-searches'] }); qc.invalidateQueries({ queryKey: ['prospect-trend'] }); qc.invalidateQueries({ queryKey: ['prospect-activity'] }) },
  })

  const promote = useMutation({
    mutationFn: async (leadIds: string[]) => {
      const res = await authedFetch('/api/prospect/promote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ searchId: selId, leadIds }) })
      const text = await res.text()
      let r: { ok?: boolean; added?: number; error?: string }
      try { r = JSON.parse(text) } catch { throw new Error(res.ok ? 'Unexpected server response.' : `Server error (${res.status}). ${text.slice(0, 140)}`) }
      if (!res.ok || r.error) throw new Error(r.error || `Failed to add (${res.status}).`)
      return r
    },
    onError: e => setError(describeError(e)),
    onSuccess: () => { setPicked(new Set()); qc.invalidateQueries({ queryKey: ['prospect-leads', selId] }); qc.invalidateQueries({ queryKey: ['prospect-placements', selId] }); qc.invalidateQueries({ queryKey: ['prospect-trend'] }); qc.invalidateQueries({ queryKey: ['prospect-activity'] }) },
  })

  const rejectLead = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('prospect_leads').update({ status: 'rejected' }).eq('id', id); if (error) throw error },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['prospect-leads', selId] }),
  })

  const visible = (leads ?? []).filter(l => filter === 'all' || l.status === filter)
  const pendingIds = visible.filter(l => l.status === 'pending').map(l => l.id)
  const toggle = (id: string) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2"><Radar className="w-5 h-5 text-gray-500" /> Auto Prospect</h1>
        <p className="text-sm text-gray-500 mt-1">Describe your ideal customer. It searches online for matching companies, scrapes every published work email off their site, keeps only verified or pattern-confirmed addresses, and feeds them into a campaign daily.</p>
      </div>
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">{error}<button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-600">✕</button></div>}

      <TrendStrip metrics={trend?.metrics} loading={trendLoading} />

      <div className="grid lg:grid-cols-[260px_1fr] gap-4 items-start">
        {/* Searches list */}
        <div className="space-y-2">
          <button onClick={() => setShowNew(v => !v)} className="btn-primary text-sm w-full flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> New search</button>
          {showNew && (
            <div className="border border-gray-200 rounded-lg p-2 space-y-2 bg-gray-50">
              <input placeholder="Search name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input py-1 text-sm" />
              <textarea placeholder="Who to find, e.g. UK accountancy firms, 10–100 staff, London" value={form.query} onChange={e => setForm(f => ({ ...f, query: e.target.value }))} rows={3} className="input py-1 text-sm" />
              <button onClick={() => createSearch.mutate()} disabled={createSearch.isPending} className="btn-secondary text-xs w-full">Create</button>
            </div>
          )}
          {(searches ?? []).map(s => {
            const st = trend?.bySearch?.[s.id]
            return (
            <button key={s.id} onClick={() => { setSelId(s.id); setPicked(new Set()) }}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${selId === s.id ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:bg-gray-50'}`}>
              <p className="font-medium text-gray-900 truncate">{s.name}</p>
              <p className="text-[11px] text-gray-400 truncate mb-1.5">{s.query}</p>
              <RowSplit metrics={[
                { label: 'Companies', tone: 'indigo', split: st?.companies ?? emptySplit() },
                { label: 'Emails', tone: 'teal', split: st?.emails ?? emptySplit() },
                { label: 'Added', tone: 'green', split: st?.added ?? emptySplit() },
              ]} />
            </button>
            )
          })}
          {!searches?.length && <p className="text-xs text-gray-400 px-1">No searches yet.</p>}
        </div>

        {/* Selected search */}
        {!sel ? <div className="card p-6"><EmptyState icon={Radar} title="Pick or create a search" description="Describe the companies and people you want to reach." /></div> : (
          <div className="space-y-4">
            {/* Settings */}
            <div className="card p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-800">{sel.name}</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => runNow.mutate()} disabled={runNow.isPending} className="btn-secondary text-xs flex items-center gap-1 disabled:opacity-50"><Play className="w-3.5 h-3.5" /> {runNow.isPending ? 'Searching…' : 'Run now'}</button>
                  <button onClick={() => confirm('Delete this search and its results?') && deleteSearch.mutate(sel.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <label className="text-xs text-gray-600 block">Sector / ideal customer (what the company does)
                <textarea defaultValue={sel.query} key={`q-${sel.id}`} rows={2} placeholder='e.g. "accountancy firms and solicitors" — keep roles/email criteria out of here' onBlur={e => e.target.value !== sel.query && updateSearch.mutate({ query: e.target.value })} className="input py-1.5 mt-0.5 text-sm" />
              </label>
              <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                <label>Location
                  <input defaultValue={sel.location ?? ''} key={`loc-${sel.id}`} placeholder="United Kingdom / London" onBlur={e => e.target.value !== (sel.location ?? '') && updateSearch.mutate({ location: e.target.value || null })} className="input py-1 mt-0.5 text-sm" />
                </label>
                <label>Company size (optional)
                  <input defaultValue={sel.company_size ?? ''} key={`sz-${sel.id}`} placeholder="e.g. 1-50 staff" onBlur={e => e.target.value !== (sel.company_size ?? '') && updateSearch.mutate({ company_size: e.target.value || null })} className="input py-1 mt-0.5 text-sm" />
                </label>
              </div>
              <div className="text-xs text-gray-600">
                <p className="mb-1">Target roles — who to capture at each company</p>
                <div className="flex flex-wrap gap-1.5">
                  {ROLE_PRESETS.map(r => {
                    const on = (sel.target_roles ?? []).includes(r)
                    return (
                      <button key={r} type="button" onClick={() => { const cur = sel.target_roles ?? []; updateSearch.mutate({ target_roles: on ? cur.filter(x => x !== r) : [...cur, r] }) }}
                        className={`px-2 py-0.5 rounded-full border text-[11px] ${on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{r}</button>
                    )
                  })}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">Empty = directors, partners, founders &amp; managers. This decides which people the AI captures at each firm.</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-gray-600">
                <label>Add per hour (max)
                  <input type="number" min={1} defaultValue={sel.hourly_target ?? 10} key={`ht-${sel.id}`} onBlur={e => Number(e.target.value) !== sel.hourly_target && updateSearch.mutate({ hourly_target: Number(e.target.value) })} className="input py-1 mt-0.5 text-sm" />
                </label>
                <label>Max per day (cap)
                  <input type="number" min={0} defaultValue={sel.daily_target} key={`dt-${sel.id}`} onBlur={e => Number(e.target.value) !== sel.daily_target && updateSearch.mutate({ daily_target: Number(e.target.value) })} className="input py-1 mt-0.5 text-sm" />
                </label>
                <label>Add from (London)
                  <input type="time" defaultValue={`${String(sel.promote_hour ?? 9).padStart(2, '0')}:${String(sel.promote_minute ?? 0).padStart(2, '0')}`} key={`pt-${sel.id}`} onBlur={e => { const [h, m] = e.target.value.split(':').map(Number); if (h !== sel.promote_hour || m !== sel.promote_minute) updateSearch.mutate({ promote_hour: h || 0, promote_minute: m || 0 }) }} className="input py-1 mt-0.5 text-sm" />
                </label>
                <label>…until (London)
                  <input type="time" defaultValue={`${String(sel.promote_end_hour ?? 15).padStart(2, '0')}:${String(sel.promote_end_minute ?? 0).padStart(2, '0')}`} key={`pte-${sel.id}`} onBlur={e => { const [h, m] = e.target.value.split(':').map(Number); if (h !== sel.promote_end_hour || m !== sel.promote_end_minute) updateSearch.mutate({ promote_end_hour: h || 0, promote_end_minute: m || 0 }) }} className="input py-1 mt-0.5 text-sm" />
                </label>
                <label className="flex items-end">
                  <span className="flex items-center gap-1"><input type="checkbox" checked={sel.promote_weekdays_only ?? true} onChange={e => updateSearch.mutate({ promote_weekdays_only: e.target.checked })} /> Weekdays only (Mon–Fri)</span>
                </label>
                <label>Max pool (stop at)
                  <input type="number" min={1} defaultValue={sel.max_pool} key={`mp-${sel.id}`} onBlur={e => Number(e.target.value) !== sel.max_pool && updateSearch.mutate({ max_pool: Number(e.target.value) })} className="input py-1 mt-0.5 text-sm" />
                </label>
                <label>Max per company / day
                  <input type="number" min={1} defaultValue={sel.per_company_daily} key={`pc-${sel.id}`} onBlur={e => Number(e.target.value) !== sel.per_company_daily && updateSearch.mutate({ per_company_daily: Number(e.target.value) })} className="input py-1 mt-0.5 text-sm" />
                </label>
                <label>Auto-add confidence
                  <select value={sel.min_confidence} onChange={e => updateSearch.mutate({ min_confidence: e.target.value })} className="input py-1 mt-0.5 text-sm">
                    <option value="pattern">Verified + pattern-confirmed</option>
                    <option value="verified">Verified only (published)</option>
                  </select>
                </label>
                <label className="flex items-end gap-3">
                  <span className="flex items-center gap-1"><input type="checkbox" checked={sel.include_personal} onChange={e => updateSearch.mutate({ include_personal: e.target.checked })} /> People</span>
                  <span className="flex items-center gap-1"><input type="checkbox" checked={sel.include_generic} onChange={e => updateSearch.mutate({ include_generic: e.target.checked })} /> Generic</span>
                </label>
              </div>

              {/* Campaign routing — one or more campaigns, staggered by days */}
              <div className="rounded-lg border border-gray-200 p-3 space-y-2">
                <p className="text-xs font-medium text-gray-700">Add leads to these campaigns</p>
                {steps.map((st, i) => (
                  <div key={i} className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-gray-400 w-10">{i === 0 ? 'First' : 'Then'}</span>
                    <select value={st.campaign_id} onChange={e => setSteps(s => s.map((x, j) => j === i ? { ...x, campaign_id: e.target.value } : x))} className="input py-1 text-sm flex-1 min-w-[160px]">
                      <option value="">— choose a campaign —</option>
                      {(campaigns ?? []).map(c => <option key={c.id} value={c.id}>{c.name} ({c.kind})</option>)}
                    </select>
                    {i === 0 ? (
                      <span className="text-[11px] text-gray-400">immediately</span>
                    ) : (
                      <span className="text-[11px] text-gray-500 flex items-center gap-1">after
                        <input type="number" min={0} value={st.delay_days} onChange={e => setSteps(s => s.map((x, j) => j === i ? { ...x, delay_days: Number(e.target.value) } : x))} className="input py-0.5 w-14 text-sm" /> days
                      </span>
                    )}
                    {steps.length > 1 && <button onClick={() => setSteps(s => s.filter((_, j) => j !== i))} className="p-1 text-gray-400 hover:text-red-600" title="Remove"><X className="w-3.5 h-3.5" /></button>}
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <button onClick={() => setSteps(s => [...s, { campaign_id: '', delay_days: 3 }])} className="text-[11px] text-brand-600 hover:underline flex items-center gap-1"><Plus className="w-3 h-3" /> Add another campaign</button>
                  <button onClick={() => saveSteps.mutate(steps)} disabled={saveSteps.isPending} className="ml-auto btn-secondary text-xs disabled:opacity-50">{saveSteps.isPending ? 'Saving…' : 'Save routing'}</button>
                </div>
                <p className="text-[10px] text-gray-400">Leads join the first campaign when added, then each later campaign that many days afterwards — staggered so campaigns don’t clash.</p>
              </div>

              <label className="text-xs text-gray-600 flex items-center gap-2">
                <input type="checkbox" checked={sel.status === 'active'} onChange={e => updateSearch.mutate({ status: e.target.checked ? 'active' : 'paused' })} />
                Active (auto-add up to the daily target each day)
              </label>
              {runNow.isPending && <p className="text-[11px] text-gray-500">Searching — each pass does one web search (~30s) and streams new leads into the table below. This runs a few passes; you can leave the page, results are saved.</p>}
              {!runNow.isPending && (note || sel.last_run_note) && <p className="text-[11px] text-gray-600">Last run: {note || sel.last_run_note}</p>}
              {!sel.campaign_id && <p className="text-[11px] text-amber-600">No campaign set — leads won’t auto-add. Add one above (use a campaign that doesn’t send, to test safely).</p>}
            </div>

            {/* Activity & stats */}
            <div className="card p-4 space-y-3">
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
                {[['Companies', stats?.companies], ['Scanned', stats?.enriched], ['Emails found', stats?.leads], ['Added to campaign', stats?.added], ['Pending', stats?.pending]].map(([label, val]) => (
                  <div key={label as string} className="rounded-lg border border-gray-100 bg-gray-50 py-2">
                    <p className="text-lg font-bold text-gray-900">{(val as number ?? 0).toLocaleString()}</p>
                    <p className="text-[10px] text-gray-500 leading-tight">{label as string}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between text-[11px] text-gray-500 flex-wrap gap-1">
                <span>Last activity: <b className="text-gray-700">{sel.last_run_at ? new Date(sel.last_run_at).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '— not run yet'}</b></span>
                <span>Adds: <b className="text-gray-700">{sel.status === 'active' ? `${sel.promote_weekdays_only ?? true ? 'Mon–Fri ' : 'daily '}${String(sel.promote_hour ?? 9).padStart(2, '0')}:${String(sel.promote_minute ?? 0).padStart(2, '0')}–${String(sel.promote_end_hour ?? 15).padStart(2, '0')}:${String(sel.promote_end_minute ?? 0).padStart(2, '0')} London (drips hourly)` : 'paused'}</b></span>
              </div>
              {readiness && (
                <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-[11px] space-y-1">
                  <div>
                    <span className="text-gray-600">Ready to auto-add now: <b className={readiness.qualified > 0 ? 'text-teal-700' : 'text-gray-700'}>{readiness.qualified}</b> of {readiness.total} pending.</span>
                    {!sel.campaign_id && <span className="text-amber-600 ml-1">No campaign in the routing — leads have nowhere to go.</span>}
                    {sel.campaign_id && readiness.qualified > 0 && sel.status === 'active' && (() => {
                      const lonNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }))
                      const nowM = lonNow.getHours() * 60 + lonNow.getMinutes()
                      const inWindow = nowM >= ((sel.promote_hour ?? 9) * 60 + (sel.promote_minute ?? 0)) && nowM < ((sel.promote_end_hour ?? 15) * 60 + (sel.promote_end_minute ?? 0))
                      const isWeekend = [0, 6].includes(lonNow.getDay())
                      const active = inWindow && !((sel.promote_weekdays_only ?? true) && isWeekend)
                      return <span className="text-gray-500 ml-1">{active ? 'Adding now — drips ~hourly.' : 'Will add during the next window.'}</span>
                    })()}
                  </div>
                  {(readiness.wrongToggle > 0 || readiness.notVerified > 0 || readiness.belowConf > 0) && (
                    <p className="text-amber-600">
                      Blocked: {[
                        readiness.wrongToggle > 0 ? `${readiness.wrongToggle} by the People/Generic toggle (enable ${sel.include_generic ? 'People' : 'Generic'} to include them)` : '',
                        readiness.notVerified > 0 ? `${readiness.notVerified} failed the MX check (domain can't receive mail)` : '',
                        readiness.belowConf > 0 ? `${readiness.belowConf} below your confidence bar (try “Verified + pattern-confirmed”)` : '',
                      ].filter(Boolean).join(' · ')}.
                    </p>
                  )}
                </div>
              )}
              <div>
                <p className="text-[11px] font-medium text-gray-600 mb-1">Recent activity</p>
                {!activity?.length ? (
                  <p className="text-[11px] text-gray-400">No activity yet — it logs each discovery, enrichment and add here. Hit “Run now” to kick it off.</p>
                ) : (
                  <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                    {activity.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 px-2.5 py-1.5 text-[11px]">
                        <span className="text-gray-400 w-24 shrink-0">{new Date(a.created_at).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        <span className={`px-1.5 py-0.5 rounded-full border text-[10px] shrink-0 ${a.kind === 'promote' ? 'bg-teal-50 text-teal-700 border-teal-200' : a.kind === 'error' ? 'bg-red-50 text-red-600 border-red-200' : a.kind === 'discover' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>{a.kind}</span>
                        <span className="text-gray-600 flex-1 break-words">{a.note}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Leads */}
            <div className="card overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1.5">
                  {(['pending', 'added', 'all'] as const).map(f => (
                    <button key={f} onClick={() => setFilter(f)} className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${filter === f ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}>{f}</button>
                  ))}
                  <span className="text-[11px] text-gray-400 ml-1">{visible.length} shown</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400 hidden sm:inline">pending = waiting to add · added = in the campaign · rejected = skipped</span>
                  {picked.size > 0 && <button onClick={() => promote.mutate([...picked])} disabled={promote.isPending} className="text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 px-3 py-1 rounded disabled:opacity-50">Add {picked.size} to campaign</button>}
                </div>
              </div>
              {!visible.length ? <div className="p-6"><EmptyState icon={Search} title="No leads yet" description="Run the search to find companies and emails." /></div> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-[11px]">
                      <tr>
                        <th className="px-3 py-2 w-8">{pendingIds.length > 0 && <input type="checkbox" checked={pendingIds.every(id => picked.has(id))} onChange={e => setPicked(e.target.checked ? new Set([...picked, ...pendingIds]) : new Set())} />}</th>
                        <th className="text-left px-3 py-2 font-medium">Person / Company</th>
                        <th className="text-left px-3 py-2 font-medium">Email</th>
                        <th className="text-left px-3 py-2 font-medium">Type</th>
                        <th className="text-left px-3 py-2 font-medium">MX</th>
                        <th className="text-left px-3 py-2 font-medium">Confidence</th>
                        <th className="text-left px-3 py-2 font-medium">Campaigns</th>
                        <th className="text-left px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visible.map(l => (
                        <tr key={l.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">{l.status === 'pending' && <input type="checkbox" checked={picked.has(l.id)} onChange={() => toggle(l.id)} />}</td>
                          <td className="px-3 py-2"><p className="font-medium text-gray-900">{l.name || <span className="text-gray-400">(generic)</span>}</p><p className="text-[11px] text-gray-400">{[l.role, l.company_name].filter(Boolean).join(' · ')}</p></td>
                          <td className="px-3 py-2 text-gray-600">{l.email}</td>
                          <td className="px-3 py-2"><span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${l.email_type === 'personal' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>{l.email_type}</span></td>
                          <td className="px-3 py-2">{l.verified ? <span className="text-green-600">✓</span> : <span className="text-red-400">✗</span>}</td>
                          <td className="px-3 py-2 align-top min-w-[240px] max-w-[320px]">
                            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full border ${l.confidence === 'verified' ? 'bg-green-50 text-green-700 border-green-200' : l.confidence === 'pattern' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>{l.confidence === 'verified' ? 'Verified' : l.confidence === 'pattern' ? 'Pattern' : (l.confidence ?? '—')}</span>
                            {l.comment && <p className="text-[11px] text-gray-500 mt-1 whitespace-normal break-words leading-snug">{l.comment}</p>}
                          </td>
                          <td className="px-3 py-2 align-top">
                            {(() => {
                              const pls = placements?.get(l.id) ?? []
                              if (!pls.length) return <span className="text-xs text-gray-300">—</span>
                              return (
                                <div className="flex flex-wrap gap-0.5 max-w-[180px]">
                                  {pls.sort((a, b) => a.step_no - b.step_no).map(p => (
                                    <span key={p.campaign_id} className="text-[9px] px-1.5 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full whitespace-nowrap" title={campName(p.campaign_id)}>{campName(p.campaign_id)}</span>
                                  ))}
                                </div>
                              )
                            })()}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${l.status === 'added' ? 'bg-green-50 text-green-700 border-green-200' : l.status === 'rejected' ? 'bg-red-50 text-red-500 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} title={l.status === 'pending' ? 'Found & verified, waiting to be added to the campaign' : l.status === 'added' ? 'Added to the campaign' : 'Rejected — will not be added'}>{l.status}</span>
                            {l.status === 'added' && l.added_at && <p className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">{new Date(l.added_at).toLocaleString('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>}
                          </td>
                          <td className="px-3 py-2 text-right">{l.status === 'pending' && <button onClick={() => rejectLead.mutate(l.id)} title="Reject" className="p-1 text-gray-400 hover:text-red-600 rounded"><X className="w-4 h-4" /></button>}{l.status === 'added' && <Check className="w-4 h-4 text-green-600 inline" />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
